import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useState } from 'react';
import Loading from '../../../components/Loading';
import MessageOdoFooterButton from '../../../components/patient/MessageOdoFooterButton';
import PatientInfo from '../../../components/orderCase/PatientInfo';
import TreatmentEstimates from '../../../components/orderCase/TreatmentEstimates';
import TreatmentPlan from '../../../components/orderCase/TreatmentPlan';
import { useLayoutFooter, useStepperContext } from '../../../layouts/PatientCreationLayout';
import { useRecentActions } from '../../../contexts/RecentActionsContext';
import { useSubmissionForm } from '../../../providers/patient/PatientSubmissionContext';
import { useToast } from '../../../providers/ToastProvider';
import { reportCaughtError } from '../../../services/errorReporting';
import { submitCase as submitCaseV2 } from '../../../services/patient/creation';
import { CASE_DESIGN_CREDIT_VALIDITY_DAYS } from '../../../utils/approvalCredit';

/** Shown when the case data carries no suggested fees of its own. */
const DEFAULT_SUGGESTED_FEES = '~$6,500 - $6,800';

const OrderCase: React.FC = () => {
    const { goNext, isV2 } = useStepperContext();
    const { setFooterContent } = useLayoutFooter();
    const { formData } = useSubmissionForm();
    const { showToast } = useToast();
    const { markActionCompleted } = useRecentActions();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const queryClient = useQueryClient();

    const handleNextClick = useCallback(async () => {
        if (!formData?.id) return;
        try {
            setIsSubmitting(true);
            // Submission runs through the v2 workflow engine, landing the case at
            // "Init - 1. Records under review".
            await submitCaseV2(formData.id);
            markActionCompleted(formData.id, 'Submit case');
            await queryClient.invalidateQueries({ queryKey: ['profile-pictures'] });
            goNext();
        } catch (error) {
            showToast('Failed to submit case. Please try again.', 'error');
            reportCaughtError(error);
        } finally {
            setIsSubmitting(false);
        }
    }, [goNext, formData?.id, queryClient, showToast, markActionCompleted]);

    React.useEffect(() => {
        setFooterContent(
            <div className="w-full flex justify-end items-center gap-3">
                <MessageOdoFooterButton />
                <button
                    onClick={handleNextClick}
                    disabled={isSubmitting}
                    className="bg-gray-800 text-white hover:bg-gray-700 px-4 py-3.5 rounded-lg font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isSubmitting ? 'Submitting...' : 'Submit case'}
                </button>
            </div>
        );
        return () => setFooterContent(null);
    }, [setFooterContent, handleNextClick, isSubmitting]);

    return (
        <>
            {isSubmitting && <Loading text="Submitting order..." />}
            <div className="max-w-4xl mx-auto">
                <div className="flex flex-row justify-center items-center">
                    <h1 className="text-2xl font-bold text-gray-900 mb-8">Submit case</h1>
                </div>
                <div className="border border-gray-200 rounded-xl p-6">
                    <div className="flex gap-6 items-stretch mb-10">
                        <div className="w-2/5">
                            <PatientInfo />
                        </div>
                        <div className="w-3/5 flex self-stretch">
                            <TreatmentEstimates
                                suggestedFees={
                                    formData?.suggestedFees
                                        ? '~'.concat(formData?.suggestedFees.replace('~', ''))
                                        : DEFAULT_SUGGESTED_FEES
                                }
                                duration={'~'.concat(
                                    (formData?.treatmentDuration ?? 'N/A').replace('~', '')
                                )}
                                appointments={formData?.subCaseType?.appointments ?? 'N/A'}
                            />
                        </div>
                    </div>
                    <div className="mb-6 p-4 bg-blue-50 border-l-4 border-blue-400 rounded-lg shadow-sm">
                        <div className="flex items-start gap-3">
                            <div className="flex-1">
                                <p className="text-sm text-gray-800 leading-relaxed">
                                    There is a{' '}
                                    <span className="font-bold text-gray-900">
                                        $200 service charge
                                    </span>{' '}
                                    for the Diagnostic Report, Case Design, and Orthodontist
                                    feedback for this case.
                                    <br className="mt-2" />
                                    <span className="italic">
                                        {`If the case is approved within ${CASE_DESIGN_CREDIT_VALIDITY_DAYS} days`}
                                    </span>{' '}
                                    (after the Diagnostic Report and Design are ready for your
                                    review), this $200 will be subtracted off your ODO case fees.
                                    {` After ${CASE_DESIGN_CREDIT_VALIDITY_DAYS} days the case credit expires.`}
                                </p>
                            </div>
                        </div>
                    </div>
                    {!isV2 && <TreatmentPlan />}
                </div>
            </div>
        </>
    );
};

export default OrderCase;
